import { CalendarOutlined } from '@ant-design/icons'
import { dayjs } from '@shared/lib/dayjs/dayjs'
import type { DateInterval } from '@shared/types'
import { Button, Flex, theme, Typography } from 'antd'
import type { CalendarRef } from 'antd-mobile'
import { Calendar, ConfigProvider, Dropdown } from 'antd-mobile'
import ruRU from 'antd-mobile/es/locales/ru-RU'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'
import { Virtuoso } from 'react-virtuoso'
import * as styles from './infinite-calendar.module.scss'

const { Text } = Typography

type Day = {
    date: string
    dayNum: string
    weekday: string
    monthLabel?: string
    hasEvents: boolean
}

const generateDays = (startDate: dayjs.Dayjs, count: number) => {
    const days: Day[] = []
    for (let i = 0; i < count; i++) {
        const date = startDate.add(i, 'day')
        days.push({
            date: date.format('YYYY-MM-DD'),
            dayNum: date.format('DD'),
            weekday: date.format('dd'),
            hasEvents: false,
            monthLabel: date.date() === 1 ? date.format('MMMM YYYY') : undefined
        })
    }
    return days
}

interface InfiniteCalendar {
    renderItem: (day: Day) => ReactNode
    onIntervalChange: ({ startDate, endDate }: DateInterval) => void
}

export const InfiniteCalendar = ({ renderItem, onIntervalChange }: InfiniteCalendar) => {
    const PRE_DAYS = 3
    const INITIAL_COUNT = 60

    const prevStartIndexRef = useRef<number | null>(null)
    const initialStart = dayjs().startOf('day').subtract(PRE_DAYS, 'day')
    const [days, setDays] = useState(() => generateDays(initialStart, INITIAL_COUNT))

    const {
        token: { colorBgContainer }
    } = theme.useToken()

    const [firstItemIndex, setFirstItemIndex] = useState(100_000)

    const prependDays = () => {
        const first = dayjs(days[0].date)
        const newEnd = first.subtract(1, 'day')
        const newStart = newEnd.startOf('month').subtract(2, 'day')
        const diff = newEnd.diff(newStart, 'day') + 1

        const newDays = generateDays(newStart, diff)
        setDays((prev) => [...newDays, ...prev])
        setFirstItemIndex((prev) => prev - diff)
    }

    const appendDays = () => {
        const last = dayjs(days[days.length - 1].date)
        const nextStart = last.add(1, 'day')
        const nextEnd = nextStart.endOf('month').add(2, 'day') // на всякий случай
        const diff = nextEnd.diff(nextStart, 'day') + 1

        const newDays = generateDays(nextStart, diff)
        setDays((prev) => [...prev, ...newDays])
    }

    const [range, setRange] = useState({
        startIndex: 0,
        endIndex: 0
    })

    const virtuosoRef = useRef<VirtuosoHandle>(null)

    const currentMonthRange = useMemo(() => {
        const start = range.startIndex - firstItemIndex
        const end = range.endIndex - firstItemIndex

        const visibleDates = days.slice(start, end + 1)
        const visibleMonths = new Set(visibleDates.map((d) => dayjs(d.date).format('YYYY-MM')))

        return Array.from(visibleMonths).map((m) => dayjs(m))
    }, [days, range, firstItemIndex])

    const calendarRef = useRef<CalendarRef>(null)

    useEffect(() => {
        if (currentMonthRange.length) {
            onIntervalChange({
                startDate: currentMonthRange[0].startOf('month').toDate(),
                endDate: currentMonthRange[1]?.endOf('month').toDate() || currentMonthRange[0].endOf('month').toDate()
            })
            calendarRef.current?.jumpTo({ year: currentMonthRange[0].year(), month: currentMonthRange[0].month() + 1 })
        }
    }, [currentMonthRange, onIntervalChange])

    const onSelectDate = useCallback(
        (val: Date | null) => {
            if (!val) return

            const selected = dayjs(val).startOf('day')
            const first = dayjs(days[0].date)
            const last = dayjs(days[days.length - 1].date)

            let newDays = [...days]
            let newFirstItemIndex = firstItemIndex

            if (selected.isBefore(first)) {
                const diff = first.diff(selected, 'day')
                const prepended = generateDays(selected, diff)
                newDays = [...prepended, ...newDays]
                newFirstItemIndex -= diff
            } else if (selected.isAfter(last)) {
                const diff = selected.diff(last, 'day')
                const appended = generateDays(last.add(1, 'day'), diff + 1).slice(1) // +1 и .slice(1) чтобы без дублирования
                newDays = [...newDays, ...appended]
                // индекс остаётся, он не двигается
            }

            setDays(newDays)
            setFirstItemIndex(newFirstItemIndex)

            // Ждём обновление стейта и потом телепорт
            setTimeout(() => {
                const index = newDays.findIndex((d) => d.date === selected.format('YYYY-MM-DD'))
                if (index !== -1) {
                    virtuosoRef.current?.scrollToIndex({
                        index,
                        align: 'start',
                        behavior: 'smooth'
                    })
                }
            }, 0)
        },
        [days, firstItemIndex]
    )

    return (
        <>
            <div className={styles.calendarHeader}>
                <Dropdown style={{ backgroundColor: colorBgContainer }}>
                    <Dropdown.Item
                        arrowIcon={null}
                        title={<Text className={styles.currentMonth}>{currentMonthRange[0]?.format('MMMM YYYY')}</Text>}
                        key="calendar"
                    >
                        <ConfigProvider locale={ruRU}>
                            <Calendar
                                ref={calendarRef}
                                weekStartsOn={'Monday'}
                                onChange={onSelectDate}
                                selectionMode="single"
                            />
                        </ConfigProvider>
                    </Dropdown.Item>
                </Dropdown>

                <Button
                    type="text"
                    onClick={() => {
                        const todayIndex = days.findIndex((d) => d.date === dayjs().format('YYYY-MM-DD'))
                        virtuosoRef.current?.scrollToIndex({ index: todayIndex })
                    }}
                >
                    <CalendarOutlined style={{ fontSize: 23 }} />
                </Button>
            </div>

            <Virtuoso
                ref={virtuosoRef}
                data={days}
                overscan={200}
                className="no-scrollbar"
                itemContent={(index, day) => (
                    <div className={styles.calendarItem}>
                        {day.monthLabel && <div className={styles.calendarMonthLabel}>{day.monthLabel}</div>}
                        <div className={styles.calendarItemContent}>
                            <Flex vertical align="start">
                                <div className={styles.calendarDayInfo}>
                                    <div className={styles.calendarDayNum}>{day.dayNum}</div>
                                    <div className={styles.calendarWeekDay}>{day.weekday}</div>
                                </div>
                            </Flex>
                            {renderItem(day)}
                        </div>
                    </div>
                )}
                startReached={prependDays}
                endReached={appendDays}
                firstItemIndex={firstItemIndex}
                style={{ flexGrow: 1 }}
                rangeChanged={({ startIndex, endIndex }) => {
                    setRange({
                        startIndex,
                        endIndex
                    })
                }}
            />
        </>
    )
}
